import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useMemo } from 'react';
import type { CivilSearcherResponse } from '~/api/civilsearch';
import type { DataRequestV2 } from '~/common/types';
import { useBulkEditAgencyNotes } from './useBulkEditAgencyNotes';
import { useBulkEditFollowUp } from './useBulkEditFollowUp';
import { useBulkEditNotes } from './useBulkEditNotes';
import { useBulkReassign } from './useBulkReassign';

export type UseBulkActionsParams = {
  civilSearchers: CivilSearcherResponse[];
  buildSearchPayloadForBulk: () => { [key: string]: any };
  getActiveQueryKeyParams: () => object;
  getDataRequestById?: (id: string) => DataRequestV2 | undefined;
};

export function useBulkActions({
  civilSearchers,
  buildSearchPayloadForBulk,
  getActiveQueryKeyParams,
  getDataRequestById,
}: UseBulkActionsParams) {
  const reassign = useBulkReassign({
    civilSearchers,
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
    getDataRequestById,
  });

  const editNotes = useBulkEditNotes({
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
  });

  const editAgencyNotes = useBulkEditAgencyNotes({
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
  });

  const editFollowUp = useBulkEditFollowUp({
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
  });

  const bulkActions = useMemo(
    () => [
      {
        label: 'Reassign',
        icon: 'shuffle' as const,
        onClick: (selection: ServerSideSelectionState) => reassign.open(selection),
      },
      {
        label: 'Add Note',
        icon: 'edit' as const,
        onClick: (selection: ServerSideSelectionState) => editNotes.open(selection),
      },
      {
        label: 'Status Update',
        icon: 'calendar' as const,
        onClick: (selection: ServerSideSelectionState) => editFollowUp.open(selection),
      },
    ],
    [reassign.open, editNotes.open, editFollowUp.open],
  );

  return {
    bulkActions,
    reassign: {
      open: reassign.isOpen,
      close: reassign.close,
      assigneeId: reassign.assigneeId,
      setAssigneeId: reassign.setAssigneeId,
      assigneeOptions: reassign.assigneeOptions,
      onSubmit: reassign.onSubmit,
      isPending: reassign.isPending,
    },
    editNotes: {
      open: editNotes.isOpen,
      close: editNotes.close,
      notes: editNotes.notes,
      setNotes: editNotes.setNotes,
      onSubmit: editNotes.onSubmit,
      isPending: editNotes.isPending,
    },
    editAgencyNotes: {
      open: editAgencyNotes.isOpen,
      close: editAgencyNotes.close,
      notes: editAgencyNotes.notes,
      setNotes: editAgencyNotes.setNotes,
      onSubmit: editAgencyNotes.onSubmit,
      isPending: editAgencyNotes.isPending,
    },
    editFollowUp: {
      open: editFollowUp.isOpen,
      close: editFollowUp.close,
      fields: editFollowUp.fields,
      setField: editFollowUp.setField,
      hasChanges: editFollowUp.hasChanges,
      onSubmit: editFollowUp.onSubmit,
      isPending: editFollowUp.isPending,
    },
  };
}
